class MegaMenuTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[data-mega-menu-tab]'));
    this.panels = Array.from(this.querySelectorAll('[data-mega-menu-panel]'));

    if (!this.tabs.length || !this.panels.length) return;

    this.tabs.forEach((tab, index) => {
      tab.addEventListener('mouseenter', () => this.activate(index));
      tab.addEventListener('focus', () => this.activate(index));
      tab.addEventListener('click', () => this.activate(index));
    });

    this.activate(0);
  }

  activate(index) {
    this.tabs.forEach((tab, tabIndex) => {
      const isActive = tabIndex === index;
      tab.classList.toggle('mega-menu__tab--active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    this.panels.forEach((panel, panelIndex) => {
      const isActive = panelIndex === index;
      panel.classList.toggle('mega-menu__panel-content--active', isActive);
      panel.hidden = !isActive;
    });

    requestAnimationFrame(() => {
      const activePanel = this.panels[index];
      activePanel?.querySelector('.mega-menu__cards-scroll')?.updateScrollState?.();
    });
  }
}

customElements.define('mega-menu-tabs', MegaMenuTabs);

function initMegaMenuCardsScroll(root = document) {
  root.querySelectorAll('.mega-menu__cards-scroll').forEach((container) => {
    if (container.dataset.scrollBound === 'true') return;
    container.dataset.scrollBound = 'true';

    const cards = container.querySelector('.mega-menu__cards');
    const prevButton = container.querySelector('.mega-menu__scroll-button--prev');
    const nextButton = container.querySelector('.mega-menu__scroll-button--next');
    if (!cards) return;

    const getScrollStep = () => {
      const card = cards.querySelector('.mega-menu__card');
      const styles = getComputedStyle(cards);
      const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;

      if (card) return card.getBoundingClientRect().width + gap;
      return Math.max(cards.clientWidth * 0.6, 120);
    };

    const updateScrollState = () => {
      const maxScrollLeft = cards.scrollWidth - cards.clientWidth;
      const canScroll = maxScrollLeft > 1;
      const isScrolled = cards.scrollLeft > 1;
      const canScrollMore = canScroll && cards.scrollLeft < maxScrollLeft - 1;

      container.classList.toggle('is-scrolled', isScrolled);
      container.classList.toggle('can-scroll-more', canScrollMore);

      if (prevButton && nextButton) {
        prevButton.hidden = !canScroll;
        nextButton.hidden = !canScroll;
        prevButton.disabled = !isScrolled;
        nextButton.disabled = !canScrollMore;
      }
    };

    container.updateScrollState = updateScrollState;

    const scrollByStep = (direction) => {
      cards.scrollBy({
        left: direction * getScrollStep(),
        behavior: 'smooth',
      });
    };

    cards.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);
    prevButton?.addEventListener('click', () => scrollByStep(-1));
    nextButton?.addEventListener('click', () => scrollByStep(1));

    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(updateScrollState);
      resizeObserver.observe(cards);
    }

    updateScrollState();
  });
}

function refreshMegaMenuCardsScroll(root = document) {
  root.querySelectorAll('.mega-menu__cards-scroll').forEach((container) => {
    container.updateScrollState?.();
  });
}

function closeMegaMenu(megaMenu) {
  megaMenu.classList.remove('mega-menu--open');
  megaMenu.querySelector('.header__menu-item--nav')?.setAttribute('aria-expanded', 'false');

  const headerWrapper = document.querySelector('.header-wrapper');
  if (headerWrapper && !document.querySelector('header-menu .mega-menu.mega-menu--open')) {
    headerWrapper.preventHide = false;
  }
}

function openMegaMenu(megaMenu) {
  document.querySelectorAll('header-menu .mega-menu.mega-menu--open').forEach((openMenu) => {
    if (openMenu === megaMenu) return;
    closeMegaMenu(openMenu);
  });

  megaMenu.classList.add('mega-menu--open');
  megaMenu.querySelector('.header__menu-item--nav')?.setAttribute('aria-expanded', 'true');

  const headerWrapper = document.querySelector('.header-wrapper');
  if (headerWrapper) {
    headerWrapper.preventHide = true;

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') === '') {
      document.documentElement.style.setProperty(
        '--header-bottom-position-desktop',
        `${Math.floor(headerWrapper.getBoundingClientRect().bottom)}px`
      );
    }
  }

  requestAnimationFrame(() => refreshMegaMenuCardsScroll(megaMenu));
}

function initMegaMenuHover() {
  const desktopQuery = window.matchMedia('(min-width: 1180px)');

  document.querySelectorAll('header-menu').forEach((headerMenu) => {
    const megaMenu = headerMenu.querySelector('.mega-menu');
    if (!megaMenu || headerMenu.dataset.megaHoverInit === 'true') return;

    headerMenu.dataset.megaHoverInit = 'true';

    let closeTimeout;

    const closeMenu = () => closeMegaMenu(megaMenu);

    const scheduleClose = () => {
      window.clearTimeout(closeTimeout);
      closeTimeout = window.setTimeout(() => {
        if (headerMenu.matches(':hover') || headerMenu.contains(document.activeElement)) return;
        closeMenu();
      }, 120);
    };

    headerMenu.addEventListener('mouseenter', () => {
      if (desktopQuery.matches) openMegaMenu(megaMenu);
    });

    headerMenu.addEventListener('mouseleave', () => {
      if (desktopQuery.matches) scheduleClose();
    });

    headerMenu.addEventListener('focusin', () => {
      if (desktopQuery.matches) openMegaMenu(megaMenu);
    });

    headerMenu.addEventListener('focusout', (event) => {
      if (!desktopQuery.matches || headerMenu.contains(event.relatedTarget)) return;
      scheduleClose();
    });
  });
}

function initMegaMenu() {
  initMegaMenuCardsScroll();
  initMegaMenuHover();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initMegaMenu);
} else {
  initMegaMenu();
}

document.addEventListener('shopify:section:load', (event) => {
  initMegaMenuCardsScroll(event.target);
  initMegaMenuHover();
});
